/**
 * One cell of the NavLog.
 *
 * Two shapes, and the difference between them IS §9.1: a cell the pilot owns is
 * an input, a cell the calculator owns is text. Nothing here can turn one into
 * the other by accident, which is the point — the working view cannot be broken
 * by a stray keystroke in a column the calculator owns, and the Formular is
 * still there for the deliberate override.
 *
 * Both shapes carry the same legend: ice ink plus a dashed rule where Aether
 * worked the value out, and a tooltip that says so in words. Colour is never
 * the only encoding.
 */
import { useState, type KeyboardEvent } from 'react';

import { inputModeFor, kindFor, label, normalize, validate } from '../../fields';
import { useT } from '../../i18n';
import { useField, usePlanStore, type FieldView } from '../../planContext';
import { useReadOnly } from '../../components/Field';
import { cellTitle, type CellTitleOpts } from './model';

/** The figure space that keeps an empty numeric column's rhythm (§9.3). */
export const BLANK = '\u2007';

/** The em dash a cell shows where a value is not merely missing but never fetched. */
export const DASH = '\u2014';

/** Enter behaves like Tab, Escape leaves the cell — the Formular's own flow. */
function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
  if (e.key === 'Escape') {
    e.currentTarget.blur();
    return;
  }
  if (e.key !== 'Enter') return;
  e.preventDefault();
  const table = e.currentTarget.closest('table');
  const all = Array.from(table?.querySelectorAll<HTMLInputElement>('input:not([readonly])') ?? []);
  const next = all[all.indexOf(e.currentTarget) + 1];
  if (next) {
    next.focus();
    next.select();
  } else {
    e.currentTarget.blur();
  }
}

/** How a stored value is shown and typed when the two are not the same string. */
export interface Transform {
  toInput(stored: string): string;
  fromInput(typed: string): string;
}

/** The class that carries the source of a value, alongside the ink. */
function markOf(f: Pick<FieldView, 'route' | 'derived' | 'auto'>): string {
  if (f.route === 'o') return ' differs';
  if (f.derived === 'aircraft-est') return ' derived est';
  if (f.route === 's' || f.derived !== '') return ' derived';
  return f.auto ? ' computed' : '';
}

export interface CellFieldProps {
  id: string;
  /** overrides the field's own label in the tooltip and for a screen reader */
  as?: string;
  transform?: Transform;
  /** this column is the calculator's, so a typed value is an override */
  computedColumn?: boolean;
  /** one more clause on the tooltip — why this cell behaves unlike its column */
  note?: string;
  placeholder?: string;
  className?: string;
}

/**
 * An editable cell.
 *
 * Typing writes straight through to the store, exactly as the Formular does, so
 * a TAS changed here moves GS and the leg time on the next frame. A value is
 * only tidied (`normalize`) and judged (`validate`) on blur — never under the
 * caret.
 *
 * A `transform` breaks that live write, and only the Sicherheitshöhe has one:
 * feet cannot be converted per keystroke ("2" would become 20 ft), so that one
 * cell holds a draft until it is left. It feeds no calculation, so nothing waits
 * for it.
 */
export function CellInput({
  id, as, transform, computedColumn, note, placeholder, className,
}: CellFieldProps) {
  const store = usePlanStore();
  const readOnly = useReadOnly();
  const t = useT();
  const f = useField(id);
  const [draft, setDraft] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  const kind = kindFor(id);
  const bad = focused ? null : validate(kind, f.value);
  const name = as ?? label(id, t);
  const opts: CellTitleOpts = {
    ...(as ? { as } : {}),
    ...(computedColumn ? { computedColumn } : {}),
    ...(note ? { note } : {}),
    invalid: bad,
  };
  const shown = draft ?? (transform ? transform.toInput(f.value) : f.value);
  const mode = inputModeFor(id);
  const upper = kind === 'icao' || kind === 'code';

  const commit = (raw: string): void => {
    const v = normalize(kind, transform ? transform.fromInput(raw) : raw);
    if (v !== f.value) store.set(id, v);
    store.recompute();
  };

  return (
    <input
      type="text"
      value={shown}
      className={'nl-in' + markOf(f) + (f.flag ? ' ' + f.flag : '') + (bad ? ' invalid' : '') +
        (className ? ' ' + className : '')}
      title={cellTitle(t, id, f, opts)}
      aria-label={name}
      aria-invalid={bad ? true : undefined}
      autoComplete="off"
      spellCheck={false}
      readOnly={readOnly}
      {...(mode ? { inputMode: mode } : {})}
      {...(upper ? { autoCapitalize: 'characters' as const, autoCorrect: 'off' } : {})}
      {...(placeholder ? { placeholder } : {})}
      onChange={e => {
        if (transform) setDraft(e.target.value);
        else store.set(id, e.target.value);
      }}
      onKeyDown={onKeyDown}
      onFocus={e => {
        setFocused(true);
        store.setActive(id);
        if (f.auto) e.target.select();
      }}
      onBlur={e => {
        setFocused(false);
        store.setActive(null);
        setDraft(null);
        if (readOnly) return;
        commit(e.target.value);
      }}
    />
  );
}

/**
 * A read-only cell: what the calculator made of the inputs, or a checkpoint the
 * row above owns. An empty one is a figure space, so the column keeps its rhythm
 * and a screen reader reads nothing.
 */
export function CellValue({ id, as, computedColumn, className }: Omit<CellFieldProps, 'transform'>) {
  const t = useT();
  const f = useField(id);
  const opts: CellTitleOpts = {
    ...(as ? { as } : {}),
    ...(computedColumn ? { computedColumn } : {}),
  };
  const empty = f.value === '';
  return (
    <span
      className={'nl-val' + markOf(f) + (f.flag ? ' ' + f.flag : '') + (className ? ' ' + className : '')}
      title={cellTitle(t, id, f, opts)}
      aria-label={as ?? label(id, t)}
    >
      {empty ? BLANK : f.value}
    </span>
  );
}

/** A value nothing in the store holds — the litres a leg burns. */
export function CellComputed({ value, title }: { value: string; title: string }) {
  return (
    <span className="nl-val computed" title={title}>
      {value === '' ? BLANK : value}
    </span>
  );
}
